import type React from 'react';
import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import type { CreateNewContractLicenseInput, LicenseInfo } from '@/domain/licenses';
import { dayStartISOInTimezone, storedDayAsLocalDate } from '@/utils/date';

import styles from './ModalUpdateLicense.module.scss';

interface FormDataState {
    licenseId?: number;
    dayFrom: Date | null;
    dayTo: Date | null;
    licenseName?: string;
}

export interface UpdateLicenseProps {
    onSubmitted: (ev: CreateNewContractLicenseInput) => void;
    licenses: LicenseInfo[];
    licenseData: CreateNewContractLicenseInput;
}

function ModalUpdateLicense({ onSubmitted, licenses, licenseData }: UpdateLicenseProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const modalId = useId();
    const licenseSelectId = useId();
    const fromDateId = useId();
    const toDateId = useId();

    const toggle = () => setOpen(!open);

    const [formData, setFormData] = useState<FormDataState>({
        ...licenseData,
        dayFrom: storedDayAsLocalDate(licenseData.dayFrom),
        dayTo: storedDayAsLocalDate(licenseData.dayTo),
        licenseName: licenses.find((l) => l.id === licenseData.licenseId)?.name,
    });

    useEffect(() => {
        if (open) {
            setFormData({
                ...licenseData,
                dayFrom: storedDayAsLocalDate(licenseData.dayFrom),
                dayTo: storedDayAsLocalDate(licenseData.dayTo),
                licenseId: licenseData.licenseId,
                licenseName: licenses.find((l) => l.id === licenseData.licenseId)?.name,
            });
        }
    }, [open, licenseData, licenses]);

    function updateForm<T extends keyof FormDataState>(key: T) {
        return (value: FormDataState[T]) =>
            setFormData((formData) => ({ ...formData, [key]: value }));
    }

    const isValid = !!formData?.dayFrom;
    const onFormDataSubmit = useCallback(
        (ev: React.FormEvent<HTMLFormElement>) => {
            const { licenseId, dayFrom, dayTo } = formData;
            if (dayFrom) {
                if (!licenseId) {
                    throw new Error('License ID is required');
                }

                onSubmitted({
                    licenseId,
                    // The days are dates, stored as the calendar day picked in any zone.
                    dayFrom: dayStartISOInTimezone(dayFrom),
                    // An open-ended licence sends null, as it always has.
                    dayTo: dayTo ? dayStartISOInTimezone(dayTo) : null,
                });
                setOpen(false);
            }
            ev.preventDefault();
        },
        [formData, onSubmitted]
    );

    return (
        <div className={styles.modalupdatelicense}>
            <Button
                variant={'secondary'}
                id={modalId}
                data-testid="openUpdateLicenseModal"
                onClick={() => setOpen(true)}>
                {t('common.actions.update')}
            </Button>
            <ModalComponent
                isOpen={open}
                onClose={toggle}
                centered={false}
                title={t('bioscopeContracts.licenseModal.updateTitle')}>
                <form onSubmit={onFormDataSubmit}>
                    <ModalBody>
                        <FormField
                            label={t('bioscopeContracts.licenseModal.license')}
                            htmlFor={licenseSelectId}>
                            <Select
                                id={licenseSelectId}
                                name="license"
                                value={
                                    formData?.licenseId != null ? String(formData.licenseId) : ''
                                }
                                onChange={(ev) => {
                                    const id = Number(ev.target.value);
                                    const selectedLicense = licenses.find((l) => l.id === id);
                                    if (selectedLicense) {
                                        updateForm('licenseId')(selectedLicense.id);
                                        setFormData((prev) => ({
                                            ...prev,
                                            licenseName: selectedLicense.name,
                                        }));
                                    }
                                }}>
                                <option value="" disabled>
                                    {t('bioscopeContracts.licenseModal.selectType')}
                                </option>
                                {licenses.map((license) => (
                                    <option key={license.id} value={String(license.id)}>
                                        {license?.name}
                                    </option>
                                ))}
                            </Select>
                            {formData?.licenseName && <div>{formData.licenseName}</div>}
                        </FormField>
                        <FormField label={t('common.labels.from')} htmlFor={fromDateId}>
                            <StyledSingleDatePicker
                                id={fromDateId}
                                required={true}
                                date={formData?.dayFrom ?? null}
                                placeholder={t('common.labels.from')}
                                onDateChange={(date) => updateForm('dayFrom')(date)}
                            />
                        </FormField>
                        <FormField label={t('molecules.common.to')} htmlFor={toDateId}>
                            <StyledSingleDatePicker
                                id={toDateId}
                                date={formData?.dayTo ?? null}
                                placeholder={t('molecules.common.to')}
                                minDate={formData?.dayFrom ?? null}
                                onDateChange={(date) => updateForm('dayTo')(date)}
                            />
                        </FormField>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="secondary"
                            onClick={(ev) => {
                                setOpen(false);
                                ev.preventDefault();
                            }}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button variant="primary" type="submit" disabled={!isValid}>
                            {t('common.actions.update')}
                        </Button>
                    </ModalFooter>
                </form>
            </ModalComponent>
        </div>
    );
}

export default ModalUpdateLicense;
